/**
 * Module quản lý chuyển đổi giao diện ngụy trang (Theme Manager)
 * Hỗ trợ 3 nền tảng:
 * 1. 'sheets': Google Sheets (Bảng tính kế toán)
 * 2. 'email': Gmail Web (Hộp thư công việc)
 * 3. 'zalo': Zalo Web (Khung chat nội bộ)
 */
import { UrlService } from '../services/urlService.js';
import { AppearanceService } from '../services/appearanceService.js';

export const THEME_KEYS = {
  HOME: 'home',
  SHEETS: 'sheets',
  EMAIL: 'email',
  ZALO: 'zalo',
  CALENDAR: 'calendar',
  CAPCUT: 'capcut',
  CANVA: 'canva',
  EMULATOR: 'emulator',
  FIGMA: 'figma',
  VSCODE: 'vscode'
};

export class ThemeManagerModule {
  constructor(storyReader, searchModal) {
    this.storyReader = storyReader;
    this.searchModal = searchModal;
    this.currentTheme = THEME_KEYS.SHEETS;

    // Modal DOM Elements
    this.themeModal = document.getElementById('theme-selector-modal');
    this.btnCloseModal = document.getElementById('theme-modal-close-btn');

    // Nút mở modal theme trên các header
    this.btnSwitchThemeSheets = document.getElementById('btn-switch-theme-sheets');
    this.btnSwitchThemeGmail = document.getElementById('btn-switch-theme-gmail');
    this.btnSwitchThemeZalo = document.getElementById('btn-switch-theme-zalo');

    // Các thẻ option trong modal
    this.optHome = document.getElementById('theme-opt-home');
    this.optCalendar = document.getElementById('theme-opt-calendar');
    this.optSheets = document.getElementById('theme-opt-sheets');
    this.optEmail = document.getElementById('theme-opt-email');
    this.optZalo = document.getElementById('theme-opt-zalo');
    this.optCapcut = document.getElementById('theme-opt-capcut');
    this.optCanva = document.getElementById('theme-opt-canva');
    this.optEmulator = document.getElementById('theme-opt-emulator');
    this.optFigma = document.getElementById('theme-opt-figma');
    this.optVscode = document.getElementById('theme-opt-vscode');

    this.bindEvents();
  }

  bindEvents() {
    // 1. Mở Modal chọn theme từ bất kỳ giao diện nào
    const openModal = () => this.openModal();

    if (this.btnSwitchThemeSheets) this.btnSwitchThemeSheets.addEventListener('click', openModal);
    if (this.btnSwitchThemeGmail) this.btnSwitchThemeGmail.addEventListener('click', openModal);
    if (this.btnSwitchThemeZalo) this.btnSwitchThemeZalo.addEventListener('click', openModal);

    // 2. Đóng Modal
    if (this.btnCloseModal) {
      this.btnCloseModal.addEventListener('click', () => this.closeModal());
    }

    // Đóng khi click ngoài nền overlay
    if (this.themeModal) {
      this.themeModal.addEventListener('click', (e) => {
        if (e.target === this.themeModal) this.closeModal();
      });
    }

    // 3. Chọn từng giao diện
    if (this.optHome) {
      this.optHome.addEventListener('click', () => this.applyTheme(THEME_KEYS.HOME, false));
    }
    if (this.optCalendar) {
      this.optCalendar.addEventListener('click', () => this.applyTheme(THEME_KEYS.CALENDAR, false));
    }
    if (this.optSheets) {
      this.optSheets.addEventListener('click', () => this.applyTheme(THEME_KEYS.SHEETS, true));
    }
    if (this.optEmail) {
      this.optEmail.addEventListener('click', () => this.applyTheme(THEME_KEYS.EMAIL, true));
    }
    if (this.optZalo) {
      this.optZalo.addEventListener('click', () => this.applyTheme(THEME_KEYS.ZALO, true));
    }
    if (this.optCapcut) {
      this.optCapcut.addEventListener('click', () => this.applyTheme(THEME_KEYS.CAPCUT, false));
    }
    if (this.optCanva) {
      this.optCanva.addEventListener('click', () => this.applyTheme(THEME_KEYS.CANVA, false));
    }
    if (this.optEmulator) {
      this.optEmulator.addEventListener('click', () => this.applyTheme(THEME_KEYS.EMULATOR, false));
    }
    if (this.optFigma) {
      this.optFigma.addEventListener('click', () => this.applyTheme(THEME_KEYS.FIGMA, false));
    }
    if (this.optVscode) {
      this.optVscode.addEventListener('click', () => this.applyTheme(THEME_KEYS.VSCODE, false));
    }

    // 4. Lắng nghe phím tắt F4 hoặc phím mở đổi theme
    window.addEventListener('keydown', (e) => {
      if (e.key === 'F4') {
        e.preventDefault();
        this.openModal();
      }
    });
  }

  openModal() {
    if (!this.themeModal) return;
    this.themeModal.style.display = 'flex';

    // Đánh dấu theme đang được chọn
    [this.optHome, this.optCalendar, this.optSheets, this.optEmail, this.optZalo, this.optCapcut, this.optCanva, this.optEmulator, this.optFigma, this.optVscode].forEach(opt => {
      if (opt) opt.classList.remove('selected');
    });

    const hash = (window.location.hash || '').toLowerCase();
    if (hash === '#/home' || hash === '#/' || this.currentTheme === THEME_KEYS.HOME) {
      if (this.optHome) this.optHome.classList.add('selected');
    } else if (hash.startsWith('#/calendar') || this.currentTheme === THEME_KEYS.CALENDAR) {
      if (this.optCalendar) this.optCalendar.classList.add('selected');
    } else if (this.currentTheme === THEME_KEYS.SHEETS && this.optSheets) {
      this.optSheets.classList.add('selected');
    } else if (this.currentTheme === THEME_KEYS.EMAIL && this.optEmail) {
      this.optEmail.classList.add('selected');
    } else if (this.currentTheme === THEME_KEYS.ZALO && this.optZalo) {
      this.optZalo.classList.add('selected');
    } else if (this.currentTheme === THEME_KEYS.CAPCUT && this.optCapcut) {
      this.optCapcut.classList.add('selected');
    } else if (this.currentTheme === THEME_KEYS.CANVA && this.optCanva) {
      this.optCanva.classList.add('selected');
    } else if ((this.currentTheme === THEME_KEYS.EMULATOR || hash.startsWith('#/emulator') || hash.startsWith('#/testing') || hash.startsWith('#/shorts')) && this.optEmulator) {
      this.optEmulator.classList.add('selected');
    } else if ((this.currentTheme === THEME_KEYS.FIGMA || hash.startsWith('#/figma') || hash.startsWith('#/prototype')) && this.optFigma) {
      this.optFigma.classList.add('selected');
    } else if ((this.currentTheme === THEME_KEYS.VSCODE || hash.startsWith('#/vscode')) && this.optVscode) {
      this.optVscode.classList.add('selected');
    }
  }

  closeModal() {
    if (this.themeModal) {
      this.themeModal.style.display = 'none';
    }
  }

  /**
   * Áp dụng giao diện mới
   * @param {string} themeName 'home' | 'sheets' | 'email' | 'zalo' | 'capcut' | 'canva'
   * @param {boolean} triggerStoryCheck Nếu true: tự động mở popup search nếu chưa có truyện
   */
  applyTheme(themeName, triggerStoryCheck = true, loadStory = true) {
    // Bước 0: Nếu là chuyển về Trang chủ Workspace Hub
    if (themeName === THEME_KEYS.HOME) {
      this.currentTheme = THEME_KEYS.HOME;
      this.closeModal();
      if (window.appRouter && typeof window.appRouter.navigateTo === 'function') {
        window.appRouter.navigateTo('#/home');
      } else {
        window.location.hash = '#/home';
      }
      return;
    }

    if (themeName === THEME_KEYS.CALENDAR) {
      this.currentTheme = THEME_KEYS.CALENDAR;
      this.closeModal();
      if (window.appRouter && typeof window.appRouter.navigateTo === 'function') {
        window.appRouter.navigateTo('#/calendar');
      } else {
        window.location.hash = '#/calendar';
      }
      return;
    }

    // Bước 1: Nếu là các theme video hoặc di động/thiết kế/code (CapCut, Canva, Emulator, Figma, VS Code), ủy quyền điều phối cho AppRouter
    if (themeName === THEME_KEYS.CAPCUT || themeName === THEME_KEYS.CANVA || themeName === THEME_KEYS.EMULATOR || themeName === THEME_KEYS.FIGMA || themeName === THEME_KEYS.VSCODE) {
      this.currentTheme = themeName;
      try {
        localStorage.setItem('fake_disguise_theme', themeName);
        if (themeName === THEME_KEYS.CAPCUT || themeName === THEME_KEYS.CANVA) {
          localStorage.setItem('fake_video_theme', themeName);
        }
      } catch (e) {}
      this.closeModal();

      if (window.appRouter && typeof window.appRouter.navigateTo === 'function') {
        window.appRouter.navigateTo(`#/${themeName}`);
      } else {
        window.location.hash = `#/${themeName}`;
      }
      return;
    }

    // Bước 2: Chuẩn hóa theme đọc truyện (Sheets, Gmail, Zalo)
    const validTheme = [THEME_KEYS.SHEETS, THEME_KEYS.EMAIL, THEME_KEYS.ZALO].includes(themeName)
      ? themeName
      : THEME_KEYS.SHEETS;

    this.currentTheme = validTheme;
    try {
      localStorage.setItem('fake_disguise_theme', validTheme);
    } catch (e) {}

    // Cập nhật đồng bộ Favicon và Title tab trình duyệt tương ứng với giao diện được chọn
    AppearanceService.updateAppearance(validTheme, this.storyReader?.lastLoadedData);

    // 1. Ẩn tất cả view, chỉ hiển thị view được chọn
    document.querySelectorAll('.theme-view').forEach(v => v.classList.remove('active'));
    const targetView = document.getElementById(`view-${validTheme}`);
    if (targetView) {
      targetView.classList.add('active');
    }

    // Dọn sạch hoàn toàn cờ mode=video và các tham số video trên URL khi chuyển về truyện
    UrlService.cleanVideoParams(validTheme);
    this.syncReaderRoute(validTheme);

    this.closeModal();

    // 2. Logic kiểm tra truyện:
    // "sau khi chọn xong sẽ mở sẵn popup search truyện nếu chưa có truyện (check trên url), nếu có rồi thì load truyện và chap"
    const params = UrlService.getParams();
    const hasStory = !!(params.storyId && params.chapterId);

    if (hasStory && loadStory) {
      // Đã có truyện trên URL => Render nội dung vào view mới
      if (this.storyReader && this.storyReader.lastLoadedData) {
        this.storyReader.renderToTheme(validTheme, this.storyReader.lastLoadedData);
      } else if (!hasStory && loadStory) {
        // Tải lại chương hiện tại từ URL
        this.storyReader.loadChapter(params.source, params.storyId, params.chapterId, false);
      }
    } else {
      // Chưa có truyện trên URL => Mở sẵn popup tìm kiếm truyện
      if (triggerStoryCheck && this.searchModal) {
        console.log('[ThemeManager] Chưa có truyện trên URL => Tự động mở popup tìm kiếm.');
        this.searchModal.open();
      }
    }
  }

  syncReaderRoute(themeName) {
    const currentHash = window.location.hash || '';
    if (!currentHash.startsWith('#/')) return;

    const queryIndex = currentHash.indexOf('?');
    const routeQuery = queryIndex >= 0 ? currentHash.slice(queryIndex) : '';
    const nextHash = `#/${themeName}${routeQuery}`;
    if (currentHash === nextHash) return;

    try {
      const currentUrl = new URL(window.location.href);
      currentUrl.hash = nextHash;
      window.history.pushState(
        { type: 'theme', theme: themeName },
        '',
        currentUrl.toString()
      );
    } catch (err) {
      console.warn('Không thể đồng bộ URL giao diện đọc truyện:', err);
    }
  }

  /**
   * Khởi tạo theme ban đầu từ URL hoặc localStorage
   */
  initInitialTheme() {
    // Nếu URL là liên kết video / YouTube, bỏ qua khởi tạo truyện để AppRouter điều phối chuẩn xác
    if (UrlService.isVideoUrl()) {
      return;
    }

    const hashRoute = (window.location.hash || '').split('?')[0].toLowerCase();
    const searchParams = new URLSearchParams(window.location.search);
    const readerParams = UrlService.getParams();
    const explicitAppParams = ['theme', 'source', 'story', 'story_id', 'chap', 'chapter', 'chapter_id', 'video', 'v', 'yt', 'time', 't', 'mode'];
    const hasExplicitAppParams = Array.from(searchParams.keys()).some(key => explicitAppParams.includes(key));

    const isRootHome = !hashRoute && !hasExplicitAppParams && !(readerParams.storyId && readerParams.chapterId);
    if (hashRoute === '#/home' || hashRoute === '#/' || isRootHome) {
      this.currentTheme = THEME_KEYS.HOME;
      return;
    }

    if (['#/sheets', '#/email', '#/zalo', '#/emulator', '#/figma', '#/vscode', '#/testing', '#/shorts', '#/prototype', '#/canva', '#/capcut', '#/calendar'].includes(hashRoute)) return;
    if (readerParams.storyId && readerParams.chapterId) return;

    const urlTheme = searchParams.get('theme');
    let savedTheme = null;
    try {
      savedTheme = localStorage.getItem('fake_disguise_theme');
    } catch (e) {}

    let initialTheme = urlTheme || savedTheme || THEME_KEYS.HOME;
    // Nếu theme đã lưu là video hoặc testing/figma/vscode nhưng không có trên route, fallback về home
    if (initialTheme === THEME_KEYS.CAPCUT || initialTheme === THEME_KEYS.CANVA || initialTheme === THEME_KEYS.EMULATOR || initialTheme === THEME_KEYS.FIGMA || initialTheme === THEME_KEYS.VSCODE) {
      initialTheme = THEME_KEYS.HOME;
    }

    this.currentTheme = initialTheme;
    if (initialTheme === THEME_KEYS.HOME) return;

    this.applyTheme(initialTheme, false);
  }

  /**
   * Alias hỗ trợ gọi chuyển theme từ AppRouter hoặc module ngoài
   * @param {string} themeName 'sheets' | 'email' | 'zalo'
   * @param {boolean} [triggerStoryCheck=false]
   */
  setTheme(themeName, triggerStoryCheck = false, loadStory = true) {
    return this.applyTheme(themeName, triggerStoryCheck, loadStory);
  }
}